import { useQuery } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'

// Types for dashboard metrics
export interface MetricsDataItem {
  date: string
  generalMetrics: Record<string, number> | null
  techMetrics: Record<string, number> | null
  billing_data: { price: number; cost: number } | null
  dialer_metrics: { total_dials: number; voip_minutes: number; dialer_status: Record<string, number> } | null
}

export interface DispositionMetricsItem {
  date: string
  rawDispositions: Record<string, number>
}

export interface DurationMetricsItem {
  date: string
  metrics: Record<string, number>
}

export interface CostMetricsItem {
  date: string
  Minutes: number
  Rate: number
  Cost: number
}

export interface CampaignMetricsItem {
  date: string
  name: string
  value: string | number
}

export interface DashboardMetricsResponse {
  campaign: {
    id: number
    name: string
    telephony: string | null
  }
  metricsData: MetricsDataItem[]
  dispositionMetrics: DispositionMetricsItem[]
  durationMetrics: DurationMetricsItem[]
  costMetrics: CostMetricsItem[]
  campaignMetrics: CampaignMetricsItem[]
}

export interface CampaignDatesResponse {
  startDate: string
  endDate: string
}

/**
 * Fetch default date range for a campaign
 */
export function useCampaignDates(campaignId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['campaign-dates', campaignId],
    queryFn: async () => {
      return apiClient<CampaignDatesResponse>(`/dashboard/dates?campaign_id=${campaignId}`)
    },
    enabled: !!campaignId && campaignId > 0,
  })
}

/**
 * Fetch dashboard metrics for a campaign and date range
 */
export function useDashboardMetrics(
  campaignId: number | null,
  startDate: string | null,
  endDate: string | null
) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['dashboard-metrics', campaignId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        campaign_id: String(campaignId),
        start_date: startDate!,
        end_date: endDate!,
      })
      return apiClient<DashboardMetricsResponse>(`/dashboard/metrics?${params}`)
    },
    enabled: !!campaignId && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}